import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Navbar from '@/components/Navbar';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { paymentsAPI } from '@/lib/api';
import { CheckCircle, Loader2, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';

export default function PaymentSuccess() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (sessionId) {
      checkPaymentStatus();
    } else {
      navigate('/dashboard');
    }
  }, [sessionId]);

  const checkPaymentStatus = async (attempt = 0) => {
    try {
      const response = await paymentsAPI.getStatus(sessionId);
      setPaymentStatus(response.data);
      
      if (response.data.payment_status === 'paid') {
        setLoading(false);
        toast.success('Payment completed successfully!');
      } else if (attempt < 5) {
        setTimeout(() => checkPaymentStatus(attempt + 1), 2000);
      } else {
        setLoading(false);
        toast.error('Payment verification timed out');
      }
    } catch (error) {
      if (attempt < 5) {
        setTimeout(() => checkPaymentStatus(attempt + 1), 2000);
      } else {
        setLoading(false);
        toast.error('Failed to verify payment status');
      }
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="max-w-2xl mx-auto px-6 md:px-12 py-16">
        <Card data-testid="payment-success-card" className="p-12 text-center">
          {loading ? (
            <>
              <Loader2 className="w-16 h-16 animate-spin mx-auto mb-6 text-accent" />
              <h1 className="text-2xl font-bold mb-2">Processing Payment...</h1>
              <p className="text-muted-foreground">Please wait while we confirm your transaction</p>
            </>
          ) : paymentStatus?.payment_status === 'paid' ? (
            <>
              <CheckCircle className="w-16 h-16 mx-auto mb-6 text-accent" />
              <h1 className="text-3xl font-bold mb-4">Payment Successful!</h1>
              <p className="text-muted-foreground mb-8">
                Your money transfer has been initiated successfully. The recipient will receive the funds shortly.
              </p>
              <div className="flex gap-4 justify-center">
                <Button data-testid="view-transactions-btn" onClick={() => navigate('/transactions')} variant="accent" className="gap-2">
                  View Transactions <ArrowRight className="w-4 h-4" />
                </Button>
                <Button data-testid="back-to-dashboard-btn" onClick={() => navigate('/dashboard')} variant="outline">
                  Back to Dashboard
                </Button>
              </div>
            </>
          ) : (
            <>
              <h1 className="text-2xl font-bold mb-4 text-destructive">Payment Verification Failed</h1>
              <p className="text-muted-foreground mb-8">
                We couldn't verify your payment. Please check your transaction history or contact support.
              </p>
              <Button data-testid="retry-btn" onClick={() => navigate('/dashboard')} variant="accent">
                Back to Dashboard
              </Button>
            </>
          )}
        </Card>
      </div>
    </div>
  );
}